<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { validatePublicQrToken } from '../qr';
import { minimumDeliveryDateValue, resolveDeliveryDeadlineFromDate } from '../delivery-date';
import TravelerOrders from './TravelerOrders.vue';
import ApartmentPicker from './ApartmentPicker.vue';
import { saveOrderReceipt } from '../order-management';
import offerImage from '../assets/hero-snack-flow.png';

type BoxEntry = {
  boxId: string;
  label: string;
  offerDisplayName: string;
  amountMinor: number;
  currency: string;
  apartmentName?: string;
  purchaseAvailable?: boolean;
};

type EntitlementStatus = 'NEW' | 'PENDING' | 'FAILED' | 'ENTITLED';
type Entitlement = {
  status: EntitlementStatus;
  paymentAttemptId?: string;
  boxId: string;
  publicQrToken: string;
};

type LockCodeDelivery = {
  lockCode: string;
  deliveryKind: string;
  redisplayCount: number;
  expiresAt: string;
};

type BoxOffer = {
  offerId: string;
  displayName: string;
  amountMinor: number;
  currency: 'EUR';
  priceVersion: string;
  products: Array<{
    name: string;
    quantity: number;
  }>;
  photoDataUrl?: string;
};

type ResumeContext = { paymentAttemptId: string; publicQrToken: string };

type AdditionalOrderStatus =
  | 'NONE'
  | 'DELIVERY_REQUEST_PENDING'
  | 'DELIVERY_REQUEST_FULFILLED'
  | 'DELIVERY_REQUEST_CANCELLED';
type AdditionalOrder = {
  status: AdditionalOrderStatus;
  deliveryRequestId?: string;
  deliveryLifecycleState?:
    | 'RECEIVED'
    | 'MODIFICATION_PROPOSEE'
    | 'REFUSEE'
    | 'PREPARING'
    | 'READY'
    | 'DELIVERED'
    | 'CANCELLED';
  selectedBoxOfferId?: string;
  selectedBoxOfferDisplayNameSnapshot?: string;
  selectedBoxOfferAmountMinorSnapshot?: number;
  selectedBoxOfferCurrencySnapshot?: 'EUR';
  selectedBoxOfferPriceVersionSnapshot?: string;
  travelerName?: string;
  apartmentName?: string;
  deliveryDeadlineAt?: string;
  absentEntryAuthorized?: boolean;
};

const props = defineProps<{
  publicToken: string;
  webMode?: boolean;
  orderMode?: boolean;
  orderConfirmationMode?: boolean;
  orderThanksMode?: boolean;
  navigate?: (url: string) => void;
  resume?: () => void;
}>();
const dedicatedOrderPath = computed(() =>
  props.webMode ? '/catalogue' : `/b/${encodeURIComponent(props.publicToken)}/order`,
);
const thankYouPath = computed(() => `${dedicatedOrderPath.value}/thanks`);
const ordersRevision = ref(0);
const orderSubmitted = ref(!props.webMode && props.orderConfirmationMode === true);
const orderCloseRequested = ref(false);
const orderCloseMessage = ref<globalThis.HTMLElement | null>(null);

const CHECKOUT_RESUME_STORAGE_KEY = 'otem-lot07-checkout-resume-v2';
const query = new globalThis.URLSearchParams(globalThis.location.search);
const forceNewOrder = props.orderMode === true && query.get('new') === '1';
const anotherOrderPath = computed(() => `${dedicatedOrderPath.value}?new=1`);
const checkoutSessionId = query.get('session_id') ?? '';
const paymentOutcome = query.get('payment') ?? '';
const isSupplementReturn = paymentOutcome === 'supplement';
const isCheckoutReturn = paymentOutcome === 'success' || paymentOutcome === 'return';
const isCheckoutCancel = paymentOutcome === 'cancel' || paymentOutcome === 'cancelled';
const box = ref<BoxEntry | null>(null);
const entitlement = ref<Entitlement | null>(null);
const delivery = ref<LockCodeDelivery | null>(null);
const additionalOrder = ref<AdditionalOrder | null>(null);
const showOrderForm = computed(
  () =>
    props.orderMode === true &&
    !orderSubmitted.value &&
    (forceNewOrder || additionalOrder.value?.status === 'NONE'),
);
const boxOffers = ref<BoxOffer[]>([]);
const catalogueReady = ref(false);
const webApartments = ref<Array<{ apartment: string; offerIds: string[] }>>([]);
const availableOffers = computed(() => {
  if (!props.webMode || !apartmentName.value) return boxOffers.value;
  const ids =
    webApartments.value.find((item) => item.apartment === apartmentName.value)?.offerIds ?? [];
  return boxOffers.value.filter((offer) => ids.includes(offer.offerId));
});

const loading = ref(true);
const checkoutStarting = ref(false);
const statusRefreshing = ref(false);
const codeLoading = ref(false);
const deliveryRequestSaving = ref(false);
const travelerName = ref('');
const travelerComment = ref('');
const apartmentName = ref('');
const deliveryDeadlineDate = ref(minimumDeliveryDateValue());
const deliveryCalendarVisible = ref(false);
const deliveryDateInput = ref<globalThis.HTMLInputElement | null>(null);
const absentEntryAuthorized = ref(false);
const selectedBoxOfferId = ref('');
watch(availableOffers, (offers) => {
  if (!offers.some((offer) => offer.offerId === selectedBoxOfferId.value))
    selectedBoxOfferId.value = '';
});
const pendingDelivery = ref<Record<string, unknown> | null>(null);
const paymentHeading = ref<globalThis.HTMLElement | null>(null);
const pendingDeliveryOffer = computed(
  () =>
    boxOffers.value.find(
      (offer) => offer.offerId === pendingDelivery.value?.selectedBoxOfferId,
    ) ?? null,
);
const deliveryPaymentStorageKey = `otem-delivery-payment-v1:${props.webMode ? 'web' : props.publicToken}`;
const webReceiptKey = 'otem-last-web-order-v1';
const selectedBoxOffer = computed(
  () => boxOffers.value.find((offer) => offer.offerId === selectedBoxOfferId.value) ?? null,
);
const error = ref('');
const resumeAvailable = ref(false);
const localSandbox = ref(false);
const deliveryPrepaymentAvailable = ref(false);
const deliveryPaymentTiming = ref(false);
let deliveryExpiryTimer: ReturnType<typeof globalThis.setTimeout> | undefined;

const busy = computed(
  () =>
    loading.value ||
    checkoutStarting.value ||
    statusRefreshing.value ||
    codeLoading.value ||
    deliveryRequestSaving.value,
);
const canStartCheckout = computed(
  () =>
    !busy.value &&
    box.value !== null &&
    box.value.purchaseAvailable !== false &&
    (entitlement.value?.status === 'NEW' || entitlement.value?.status === 'FAILED'),
);
const canResumeCheckout = computed(
  () => !busy.value && entitlement.value?.status === 'PENDING' && resumeAvailable.value,
);
const deliveryValidationMessage = computed(() => {
  if (!availableOffers.value.some((offer) => offer.offerId === selectedBoxOfferId.value)) {
    return 'Choisissez une offre de Box.';
  }
  if (!travelerName.value.trim()) return 'Saisissez votre nom.';
  if (!apartmentName.value.trim()) return 'Saisissez l’appartement ou le nom du logement.';
  if (resolveDeliveryDeadlineFromDate(deliveryDeadlineDate.value) === null) {
    return 'Choisissez une date située au moins 24 h dans le futur.';
  }
  if (!absentEntryAuthorized.value) {
    return 'Votre autorisation explicite est nécessaire pour organiser la livraison.';
  }
  return '';
});
const canSubmitDeliveryRequest = computed(
  () => !busy.value && deliveryValidationMessage.value === '',
);
const minimumDeliveryDate = computed(() => minimumDeliveryDateValue());
const formattedDeliveryDate = computed(() => {
  const date = resolveDeliveryDeadlineFromDate(deliveryDeadlineDate.value);
  return date
    ? new Intl.DateTimeFormat('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      }).format(date)
    : 'À choisir';
});

function showDeliveryDatePicker(event?: globalThis.MouseEvent): void {
  const input = deliveryDateInput.value;
  if (!input) return;
  input.focus();
  try {
    if (typeof input.showPicker === 'function') {
      input.showPicker();
      event?.preventDefault();
    }
  } catch {
    // Native date entry remains available when the browser does not allow showPicker.
  }
}
async function openDeliveryCalendar(): Promise<void> {
  deliveryCalendarVisible.value = true;
  await nextTick();
  showDeliveryDatePicker();
}

function restoreDefaultDeliveryDate(): void {
  deliveryDeadlineDate.value = minimumDeliveryDateValue();
  deliveryCalendarVisible.value = false;
}

function closeOrderFlow(): void {
  orderCloseRequested.value = true;
  void nextTick(() => orderCloseMessage.value?.focus());
  globalThis.close();
}
const formattedPrice = computed(() => {
  if (!box.value) return '';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: box.value.currency,
  }).format(box.value.amountMinor / 100);
});
const redisplaysRemaining = computed(() =>
  Math.max(0, 3 - (delivery.value?.redisplayCount ?? 0)),
);

function formatAmount(amountMinor: number, currency: 'EUR'): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency,
  }).format(amountMinor / 100);
}

function photoForOffer(offer: BoxOffer): string {
  return offer.photoDataUrl ?? offerImage;
}
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

async function responseJson(
  response: Awaited<ReturnType<typeof globalThis.fetch>>,
): Promise<Record<string, unknown>> {
  const body: unknown = await response.json();
  if (!isRecord(body)) throw new Error('Réponse serveur invalide.');
  return body;
}

function responseMessage(body: Record<string, unknown>, fallback: string): string {
  if (body.error === 'DELIVERY_DEADLINE_MINIMUM_24H') {
    return 'La date limite doit être située au moins 24 h après la création de la demande.';
  }
  if (body.error === 'ABSENT_ENTRY_AUTHORIZATION_REQUIRED') {
    return 'Votre autorisation explicite est nécessaire pour organiser la livraison.';
  }
  if (body.error === 'DELIVERY_APARTMENT_MISMATCH') {
    return 'Le logement doit correspondre à l’appartement opérationnel de cette Box.';
  }
  if (body.error === 'REQUEST_REJECTED') {
    return 'La demande n’a pas pu être validée. Vérifiez les informations puis réessayez.';
  }
  return typeof body.error === 'string' && body.error ? body.error : fallback;
}

function validatedCheckoutUrl(value: unknown): string {
  if (typeof value !== 'string' || value.length > 2048) {
    throw new Error('Destination de paiement non sécurisée.');
  }
  const url = new globalThis.URL(value);
  if (url.protocol !== 'https:' || !url.hostname || url.username || url.password) {
    throw new Error('Destination de paiement non sécurisée.');
  }
  return url.toString();
}

function clearResume(): void {
  resumeAvailable.value = false;
  try {
    globalThis.sessionStorage.removeItem(CHECKOUT_RESUME_STORAGE_KEY);
  } catch {
    // Server state remains authoritative when browser storage is blocked.
  }
}

function retainResume(context: ResumeContext): void {
  resumeAvailable.value = true;
  try {
    globalThis.sessionStorage.setItem(CHECKOUT_RESUME_STORAGE_KEY, JSON.stringify(context));
  } catch {
    // The in-memory marker still supports the immediate Back flow.
  }
}

function restoreResume(paymentAttemptId: string): void {
  resumeAvailable.value = false;
  try {
    const stored: unknown = JSON.parse(
      globalThis.sessionStorage.getItem(CHECKOUT_RESUME_STORAGE_KEY) ?? 'null',
    );
    if (
      !isRecord(stored) ||
      stored.paymentAttemptId !== paymentAttemptId ||
      stored.publicQrToken !== props.publicToken
    ) {
      clearResume();
      return;
    }
    resumeAvailable.value = true;
  } catch {
    clearResume();
  }
}

function retainDelivery(value: LockCodeDelivery): void {
  const remaining = Date.parse(value.expiresAt) - Date.now();
  if (!Number.isFinite(remaining) || remaining <= 0) throw new Error('Code expiré.');
  delivery.value = value;
  deliveryExpiryTimer = globalThis.setTimeout(() => {
    delivery.value = null;
  }, remaining);
}

function validateEntitlement(body: Record<string, unknown>): Entitlement {
  if (
    !['NEW', 'PENDING', 'FAILED', 'ENTITLED'].includes(String(body.status)) ||
    typeof body.boxId !== 'string' ||
    body.publicQrToken !== props.publicToken ||
    (body.status !== 'NEW' && typeof body.paymentAttemptId !== 'string')
  ) {
    throw new Error('Statut serveur invalide.');
  }
  return {
    status: body.status as EntitlementStatus,
    ...(typeof body.paymentAttemptId === 'string'
      ? { paymentAttemptId: body.paymentAttemptId }
      : {}),
    boxId: body.boxId,
    publicQrToken: body.publicQrToken,
  };
}

function validateAdditionalOrder(body: Record<string, unknown>): AdditionalOrder {
  const status = String(body.status) as AdditionalOrderStatus;
  if (
    ![
      'NONE',
      'DELIVERY_REQUEST_PENDING',
      'DELIVERY_REQUEST_FULFILLED',
      'DELIVERY_REQUEST_CANCELLED',
    ].includes(status)
  ) {
    throw new Error('Statut de commande invalide.');
  }
  if (
    status !== 'NONE' &&
    (typeof body.deliveryRequestId !== 'string' ||
      ![
        'RECEIVED',
        'MODIFICATION_PROPOSEE',
        'REFUSEE',
        'PREPARING',
        'READY',
        'DELIVERED',
        'CANCELLED',
      ].includes(String(body.deliveryLifecycleState)) ||
      typeof body.selectedBoxOfferId !== 'string' ||
      typeof body.selectedBoxOfferDisplayNameSnapshot !== 'string' ||
      !Number.isSafeInteger(body.selectedBoxOfferAmountMinorSnapshot) ||
      body.selectedBoxOfferCurrencySnapshot !== 'EUR' ||
      typeof body.selectedBoxOfferPriceVersionSnapshot !== 'string' ||
      typeof body.travelerName !== 'string' ||
      typeof body.apartmentName !== 'string' ||
      typeof body.deliveryDeadlineAt !== 'string' ||
      body.absentEntryAuthorized !== true)
  ) {
    throw new Error('Statut de commande invalide.');
  }
  return {
    status,
    ...(typeof body.deliveryRequestId === 'string'
      ? { deliveryRequestId: body.deliveryRequestId }
      : {}),
    ...(typeof body.deliveryLifecycleState === 'string'
      ? {
          deliveryLifecycleState:
            body.deliveryLifecycleState as AdditionalOrder['deliveryLifecycleState'],
        }
      : {}),
    ...(typeof body.selectedBoxOfferId === 'string'
      ? { selectedBoxOfferId: body.selectedBoxOfferId }
      : {}),
    ...(typeof body.selectedBoxOfferDisplayNameSnapshot === 'string'
      ? { selectedBoxOfferDisplayNameSnapshot: body.selectedBoxOfferDisplayNameSnapshot }
      : {}),
    ...(Number.isSafeInteger(body.selectedBoxOfferAmountMinorSnapshot)
      ? {
          selectedBoxOfferAmountMinorSnapshot: Number(body.selectedBoxOfferAmountMinorSnapshot),
        }
      : {}),
    ...(body.selectedBoxOfferCurrencySnapshot === 'EUR'
      ? { selectedBoxOfferCurrencySnapshot: 'EUR' as const }
      : {}),
    ...(typeof body.selectedBoxOfferPriceVersionSnapshot === 'string'
      ? { selectedBoxOfferPriceVersionSnapshot: body.selectedBoxOfferPriceVersionSnapshot }
      : {}),
    ...(typeof body.travelerName === 'string' ? { travelerName: body.travelerName } : {}),
    ...(typeof body.apartmentName === 'string' ? { apartmentName: body.apartmentName } : {}),
    ...(typeof body.deliveryDeadlineAt === 'string'
      ? { deliveryDeadlineAt: body.deliveryDeadlineAt }
      : {}),
    ...(body.absentEntryAuthorized === true ? { absentEntryAuthorized: true } : {}),
  };
}

function validateBoxOffer(value: unknown): value is BoxOffer {
  if (!isRecord(value)) return false;
  return (
    typeof value.offerId === 'string' &&
    typeof value.displayName === 'string' &&
    Number.isSafeInteger(value.amountMinor) &&
    value.currency === 'EUR' &&
    typeof value.priceVersion === 'string' &&
    Array.isArray(value.products) &&
    value.products.every(
      (product) =>
        isRecord(product) &&
        typeof product.name === 'string' &&
        Number.isSafeInteger(product.quantity) &&
        Number(product.quantity) >= 0,
    ) &&
    (value.photoDataUrl === undefined || typeof value.photoDataUrl === 'string')
  );
}

async function loadBoxOffers(): Promise<void> {
  const response = await globalThis.fetch('/api/box-offers', { credentials: 'include' });
  const body = await responseJson(response);
  if (
    !response.ok ||
    !Array.isArray(body.boxOffers) ||
    !body.boxOffers.every(validateBoxOffer)
  ) {
    throw new Error(responseMessage(body, 'Offres de Box indisponibles.'));
  }
  boxOffers.value = body.boxOffers;
}

async function refreshAdditionalOrder(): Promise<void> {
  const response = await globalThis.fetch(
    `/api/additional-orders/current?boxToken=${encodeURIComponent(props.publicToken)}`,
    { credentials: 'include' },
  );
  const body = await responseJson(response);
  if (!response.ok) throw new Error(responseMessage(body, 'Commande indisponible.'));
  additionalOrder.value = validateAdditionalOrder(body);
}

async function submitDeliveryRequest(): Promise<void> {
  if (busy.value) return;
  const normalizedTravelerName = travelerName.value.trim();
  const normalizedApartmentName = apartmentName.value.trim();
  const deadline = resolveDeliveryDeadlineFromDate(deliveryDeadlineDate.value);
  if (!availableOffers.value.some((offer) => offer.offerId === selectedBoxOfferId.value)) {
    error.value = 'Choisissez une offre de Box.';
    return;
  }
  if (!normalizedTravelerName) {
    error.value = 'Saisissez votre nom.';
    return;
  }
  if (!normalizedApartmentName) {
    error.value = 'Saisissez l’appartement ou le nom du logement.';
    return;
  }
  if (deadline === null) {
    error.value = 'Choisissez une date située au moins 24 h dans le futur.';
    return;
  }
  if (!absentEntryAuthorized.value) {
    error.value = 'Votre autorisation explicite est nécessaire pour organiser la livraison.';
    return;
  }
  if (!deliveryPrepaymentAvailable.value) {
    error.value =
      'Le prépaiement des livraisons n’est pas encore disponible dans cet environnement.';
    return;
  }
  const payload = {
    idempotencyKey: globalThis.crypto.randomUUID(),
    ...(props.webMode ? {} : { publicQrToken: props.publicToken }),
    selectedBoxOfferId: selectedBoxOfferId.value,
    travelerName: normalizedTravelerName,
    ...(travelerComment.value.trim() ? { travelerComment: travelerComment.value.trim() } : {}),
    apartmentName: normalizedApartmentName,
    deliveryDeadlineAt: deadline.toISOString(),
    absentEntryAuthorized: true,
  };
  try {
    globalThis.sessionStorage.setItem(deliveryPaymentStorageKey, JSON.stringify(payload));
  } catch {
    error.value = 'Autorisez le stockage de session pour pouvoir reprendre votre commande.';
    return;
  }
  pendingDelivery.value = payload;
  error.value = '';
  if (!localSandbox.value) {
    await resumeDeliveryPayment();
    return;
  }
  globalThis.history.replaceState({}, '', `${dedicatedOrderPath.value}?new=1&step=payment`);
  await nextTick();
  paymentHeading.value?.focus();
}

async function resumeDeliveryPayment(returnVerification = false): Promise<void> {
  if (
    (busy.value &&
      !(returnVerification === true && loading.value && !deliveryRequestSaving.value)) ||
    !pendingDelivery.value ||
    !deliveryPrepaymentAvailable.value
  )
    return;
  deliveryRequestSaving.value = true;
  error.value = '';
  try {
    const response = await globalThis.fetch(
      props.webMode
        ? '/api/catalogue/delivery-requests'
        : '/api/additional-orders/delivery-requests',
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(pendingDelivery.value),
      },
    );
    const body = await responseJson(response);
    if (!response.ok) throw new Error(responseMessage(body, 'Demande de livraison refusée.'));
    if (
      body.paymentStatus === 'PENDING' &&
      body.status === 'PAYMENT_PENDING' &&
      !localSandbox.value
    ) {
      if (returnVerification === true) {
        error.value =
          'Le paiement est encore en attente. Vous pouvez reprendre la même session.';
      } else {
        const url = validatedCheckoutUrl(body.checkoutUrl);
        (props.navigate ?? ((value: string) => globalThis.location.assign(value)))(url);
      }
      return;
    }
    if (!['PAID', 'AUTHORIZED'].includes(String(body.paymentStatus)))
      throw new Error('Le paiement de la commande n’est pas confirmé.');
    additionalOrder.value = validateAdditionalOrder(body);
    orderSubmitted.value = true;
    try {
      saveOrderReceipt({
        deliveryRequestId: String(body.deliveryRequestId),
        credential: String(pendingDelivery.value.idempotencyKey),
        travelerName:
          typeof pendingDelivery.value.travelerName === 'string'
            ? pendingDelivery.value.travelerName
            : undefined,
      });
      if (props.webMode)
        globalThis.sessionStorage.setItem(
          webReceiptKey,
          JSON.stringify({
            deliveryRequestId: body.deliveryRequestId,
            credential: pendingDelivery.value.idempotencyKey,
          }),
        );
      ordersRevision.value += 1;
    } catch {
      error.value =
        'Commande enregistrée. Le suivi privé ne peut pas être conservé sur ce navigateur. Contactez l’équipe pour une annulation.';
    }
    pendingDelivery.value = null;
    try {
      globalThis.sessionStorage.removeItem(deliveryPaymentStorageKey);
    } catch {
      /* A replay remains idempotent. */
    }
    globalThis.history.pushState({}, '', `${dedicatedOrderPath.value}/confirmed`);
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Demande de livraison impossible.';
  } finally {
    deliveryRequestSaving.value = false;
  }
}

async function displayLockCode(allowDuringRefresh = false): Promise<void> {
  if (entitlement.value?.status !== 'ENTITLED' || (busy.value && !allowDuringRefresh)) return;
  codeLoading.value = true;
  error.value = '';
  try {
    const response = await globalThis.fetch('/api/lock-code/display', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ publicQrToken: props.publicToken }),
    });
    const body = await responseJson(response);
    if (!response.ok) throw new Error(responseMessage(body, 'Code indisponible.'));
    if (
      typeof body.lockCode !== 'string' ||
      !/^\d{3}$/.test(body.lockCode) ||
      typeof body.deliveryKind !== 'string' ||
      !Number.isSafeInteger(body.redisplayCount) ||
      typeof body.expiresAt !== 'string'
    ) {
      throw new Error('Code serveur invalide.');
    }
    retainDelivery(body as LockCodeDelivery);
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Code indisponible.';
  } finally {
    codeLoading.value = false;
  }
}

async function applyEntitlement(next: Entitlement): Promise<void> {
  entitlement.value = next;
  if (next.status === 'PENDING' && next.paymentAttemptId) {
    restoreResume(next.paymentAttemptId);
  } else {
    clearResume();
  }
  if (next.status === 'ENTITLED' && delivery.value === null && !props.orderMode) {
    await displayLockCode(true);
  }
}

async function refreshEntitlement(): Promise<void> {
  if (busy.value && !loading.value) return;
  statusRefreshing.value = true;
  error.value = '';
  try {
    const response = await globalThis.fetch(
      `/api/entitlements/current?boxToken=${encodeURIComponent(props.publicToken)}`,
      { credentials: 'include' },
    );
    const body = await responseJson(response);
    if (!response.ok) throw new Error(responseMessage(body, 'Statut indisponible.'));
    await applyEntitlement(validateEntitlement(body));
  } catch (cause) {
    entitlement.value = null;
    error.value = cause instanceof Error ? cause.message : 'Statut indisponible.';
  } finally {
    statusRefreshing.value = false;
  }
}

async function verifyCheckoutReturn(): Promise<void> {
  if (checkoutSessionId.length > 255 || !/^cs_[A-Za-z0-9_]+$/.test(checkoutSessionId)) {
    throw new Error('Référence de paiement invalide.');
  }
  const response = await globalThis.fetch('/api/payment/checkout/verify', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      checkoutSessionId,
      publicQrToken: props.publicToken,
    }),
  });
  const body = await responseJson(response);
  if (!response.ok) throw new Error(responseMessage(body, 'Paiement non vérifié.'));
  await applyEntitlement(validateEntitlement(body));
}

async function startCheckout(): Promise<void> {
  if (!canStartCheckout.value) return;
  checkoutStarting.value = true;
  error.value = '';
  try {
    const response = await globalThis.fetch('/api/payment-attempts', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ publicQrToken: props.publicToken }),
    });
    const body = await responseJson(response);
    if (!response.ok) throw new Error(responseMessage(body, 'Paiement indisponible.'));
    if (typeof body.paymentAttemptId !== 'string' || body.status !== 'PENDING') {
      throw new Error('Session de paiement invalide.');
    }
    if (localSandbox.value) {
      if (
        body.paymentMode !== 'local-sandbox' ||
        typeof body.checkoutSessionId !== 'string' ||
        !/^cs_local_[A-Za-z0-9_-]+$/.test(body.checkoutSessionId)
      ) {
        throw new Error('Session de paiement invalide.');
      }
      const completedResponse = await globalThis.fetch('/api/payment/local-sandbox/complete', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          checkoutSessionId: body.checkoutSessionId,
          publicQrToken: props.publicToken,
        }),
      });
      const completed = await responseJson(completedResponse);
      if (!completedResponse.ok) {
        throw new Error(responseMessage(completed, 'Paiement non vérifié.'));
      }
      await applyEntitlement(validateEntitlement(completed));
      await loadBoxOffers();
      await refreshAdditionalOrder();
      return;
    }
    const checkoutUrl = validatedCheckoutUrl(body.checkoutUrl);
    entitlement.value = {
      status: 'PENDING',
      paymentAttemptId: body.paymentAttemptId,
      boxId: box.value?.boxId ?? '',
      publicQrToken: props.publicToken,
    };
    retainResume({
      paymentAttemptId: body.paymentAttemptId,
      publicQrToken: props.publicToken,
    });
    (props.navigate ?? ((url: string) => globalThis.location.assign(url)))(checkoutUrl);
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Paiement indisponible.';
  } finally {
    checkoutStarting.value = false;
  }
}

function resumeCheckout(): void {
  if (!canResumeCheckout.value) return;
  (props.resume ?? (() => globalThis.history.forward()))();
}

async function loadWebCatalogue(): Promise<void> {
  const response = await globalThis.fetch('/api/catalogue', { credentials: 'include' });
  const body = await responseJson(response);
  if (
    !response.ok ||
    !Array.isArray(body.boxOffers) ||
    !body.boxOffers.every(validateBoxOffer) ||
    !Array.isArray(body.apartments) ||
    !body.apartments.every(
      (item) =>
        isRecord(item) &&
        typeof item.apartment === 'string' &&
        Array.isArray(item.offerIds) &&
        item.offerIds.every((id) => typeof id === 'string'),
    ) ||
    (body.paymentMode !== undefined && body.paymentMode !== 'local-sandbox')
  ) {
    throw new Error('Catalogue indisponible.');
  }
  boxOffers.value = body.boxOffers;
  webApartments.value = body.apartments as Array<{ apartment: string; offerIds: string[] }>;
  localSandbox.value = body.paymentMode === 'local-sandbox';
  deliveryPaymentTiming.value = body.deliveryPaymentTiming === true;
  deliveryPrepaymentAvailable.value = body.deliveryPrepaymentAvailable === true;
  additionalOrder.value = { status: 'NONE' };
  catalogueReady.value = true;
  try {
    const raw = globalThis.sessionStorage.getItem(deliveryPaymentStorageKey);
    const pending = raw ? JSON.parse(raw) : null;
    if (
      isRecord(pending) &&
      !Object.hasOwn(pending, 'publicQrToken') &&
      typeof pending.idempotencyKey === 'string'
    )
      pendingDelivery.value = pending;
  } catch {
    /* Submission checks writable storage before any payment. */
  }
  if (pendingDelivery.value && isCheckoutReturn && !localSandbox.value) {
    await resumeDeliveryPayment(true);
  } else if (!forceNewOrder && (props.orderConfirmationMode || props.orderThanksMode)) {
    let receipt;
    try {
      receipt = JSON.parse(globalThis.sessionStorage.getItem(webReceiptKey) ?? 'null');
    } catch {
      /* No private proof. */
    }
    if (
      !isRecord(receipt) ||
      typeof receipt.deliveryRequestId !== 'string' ||
      typeof receipt.credential !== 'string'
    )
      return;
    const verification = await globalThis.fetch(
      '/api/orders/' + encodeURIComponent(receipt.deliveryRequestId) + '/status',
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ credential: receipt.credential }),
      },
    );
    const confirmed = await responseJson(verification);
    if (
      !verification.ok ||
      confirmed.deliveryRequestId !== receipt.deliveryRequestId ||
      ![
        'RECEIVED',
        'MODIFICATION_PROPOSEE',
        'REFUSEE',
        'PREPARING',
        'READY',
        'DELIVERED',
        'CANCELLED',
      ].includes(String(confirmed.deliveryLifecycleState))
    ) {
      throw new Error(
        'La commande ne peut pas être vérifiée. Retrouvez vos demandes ci-dessous.',
      );
    }
    additionalOrder.value = {
      status:
        confirmed.deliveryLifecycleState === 'CANCELLED'
          ? 'DELIVERY_REQUEST_CANCELLED'
          : 'DELIVERY_REQUEST_PENDING',
      deliveryRequestId: receipt.deliveryRequestId,
      deliveryLifecycleState:
        confirmed.deliveryLifecycleState as AdditionalOrder['deliveryLifecycleState'],
    };
    pendingDelivery.value = null;
    orderSubmitted.value = true;
  }
}

onMounted(async () => {
  try {
    if (isSupplementReturn) return;
    if (props.webMode) {
      await loadWebCatalogue();
      return;
    }
    validatePublicQrToken(props.publicToken);
    if (props.orderThanksMode === true) return;
    const response = await globalThis.fetch(
      `/api/box-entry/${encodeURIComponent(props.publicToken)}`,
      {
        credentials: 'include',
      },
    );
    const body = await responseJson(response);
    if (!response.ok) throw new Error(responseMessage(body, 'Box introuvable.'));
    if (
      typeof body.boxId !== 'string' ||
      typeof body.label !== 'string' ||
      typeof body.offerDisplayName !== 'string' ||
      !Number.isSafeInteger(body.amountMinor) ||
      body.currency !== 'EUR' ||
      (body.apartmentName !== undefined && typeof body.apartmentName !== 'string')
    ) {
      throw new Error('Box serveur invalide.');
    }
    if (body.paymentMode !== undefined && body.paymentMode !== 'local-sandbox') {
      throw new Error('Mode de paiement serveur invalide.');
    }
    localSandbox.value = body.paymentMode === 'local-sandbox';
    deliveryPaymentTiming.value = body.deliveryPaymentTiming === true;
    deliveryPrepaymentAvailable.value =
      localSandbox.value || body.deliveryPrepaymentAvailable === true;
    box.value = {
      boxId: body.boxId,
      label: body.label,
      offerDisplayName: body.offerDisplayName,
      amountMinor: body.amountMinor,
      currency: body.currency,
      purchaseAvailable: body.purchaseAvailable !== false,
      ...(typeof body.apartmentName === 'string' ? { apartmentName: body.apartmentName } : {}),
    } as BoxEntry;
    if (props.orderMode === true && typeof body.apartmentName === 'string') {
      apartmentName.value = body.apartmentName;
    }
    if (props.orderMode === true) {
      try {
        const raw = globalThis.sessionStorage.getItem(deliveryPaymentStorageKey);
        const pending = raw ? JSON.parse(raw) : null;
        if (
          pending?.publicQrToken === props.publicToken &&
          typeof pending.idempotencyKey === 'string'
        )
          pendingDelivery.value = pending;
      } catch {
        /* A fresh order requires writable session storage before submission. */
      }
      await loadBoxOffers();
      await refreshAdditionalOrder();
      if (pendingDelivery.value && isCheckoutReturn && !localSandbox.value)
        await resumeDeliveryPayment(true);
    } else {
      if (isCheckoutReturn) await verifyCheckoutReturn();
      else await refreshEntitlement();
      if (entitlement.value?.status === 'ENTITLED') {
        await loadBoxOffers();
        await refreshAdditionalOrder();
      }
    }
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Box indisponible.';
  } finally {
    loading.value = false;
  }
});

onUnmounted(() => {
  if (deliveryExpiryTimer !== undefined) globalThis.clearTimeout(deliveryExpiryTimer);
});
</script>

<template>
  <main id="main" class="traveler-shell">
    <header class="traveler-brand" aria-label="OTEM Upsells Manager">
      <span class="traveler-brand-mark" aria-hidden="true">O</span>
      <span>OTEM Upsells Manager</span>
    </header>
    <section v-if="isSupplementReturn" class="traveler-card" aria-labelledby="traveler-title">
      <p class="eyebrow">OTEM · Votre commande</p>
      <h1 id="traveler-title" class="traveler-title">Votre modification</h1>
      <p>
        Retrouvez ci-dessous le règlement et la demande de modification vérifiés auprès du
        serveur.
      </p>
      <a class="button button-secondary traveler-secondary" :href="anotherOrderPath"
        >Accéder au catalogue</a
      >
    </section>
    <section v-else class="traveler-card" aria-labelledby="traveler-title">
      <template
        v-if="additionalOrder?.deliveryLifecycleState === 'CANCELLED' && !forceNewOrder"
      >
        <h1 id="traveler-title" class="traveler-title">Commande annulée</h1>
        <p>
          Cette commande ne sera pas livrée. Vous pouvez accéder à vos demandes depuis « Annuler
          / modifier une commande ».
        </p>
        <a class="button button-primary traveler-primary" :href="anotherOrderPath"
          >Accéder au catalogue</a
        >
      </template>
      <template v-else-if="props.orderThanksMode && (!props.webMode || orderSubmitted)">
        <p class="eyebrow">OTEM · Commande voyageur</p>
        <h1 id="traveler-title" class="traveler-title">Merci pour votre commande.</h1>
        <p class="order-confirmation-only" role="status">
          Votre commande est enregistrée. Vous pouvez demander une annulation ou une
          modification ci-dessous.
        </p>
        <button
          v-if="!orderCloseRequested"
          class="button button-primary traveler-primary"
          data-testid="close-order-flow"
          type="button"
          @click="closeOrderFlow"
        >
          Fermer
        </button>
        <p
          v-else
          ref="orderCloseMessage"
          data-testid="order-close-message"
          role="status"
          tabindex="-1"
        >
          Votre demande est bien enregistrée. Vous pouvez fermer cet onglet et reprendre votre
          séjour.
        </p>
      </template>
      <template v-else-if="!props.orderMode">
        <p class="eyebrow">OTEM · Interface voyageur</p>
        <h1 id="traveler-title" class="traveler-title">
          {{ box?.offerDisplayName ?? 'Votre offre' }}
        </h1>
        <p v-if="box" class="traveler-box-code">{{ box.label }}</p>
      </template>
      <template v-else-if="pendingDelivery">
        <p class="eyebrow">OTEM · Commande voyageur</p>
        <h1 id="traveler-title" ref="paymentHeading" class="traveler-title" tabindex="-1">
          {{ localSandbox ? 'Paiement de test' : 'Paiement sécurisé' }}
        </h1>
      </template>
      <template
        v-else-if="
          orderSubmitted ||
          (!forceNewOrder && additionalOrder !== null && additionalOrder.status !== 'NONE')
        "
      >
        <p class="eyebrow">OTEM · Commande voyageur</p>
        <h1 id="traveler-title" class="traveler-title">Box enregistrée</h1>
        <p class="order-confirmation-only" role="status">
          Votre demande de Box est enregistrée. Vous pouvez demander une annulation ou une
          modification ci-dessous.
        </p>
        <a
          class="button button-primary traveler-primary"
          data-testid="order-another-product"
          :href="anotherOrderPath"
        >
          Commander un autre produit ?
        </a>
        <a
          class="button button-secondary traveler-secondary traveler-confirm"
          data-testid="confirm-order-flow"
          :href="thankYouPath"
        >
          Confirmer
        </a>
      </template>
      <template v-else>
        <p class="eyebrow">OTEM · Commande voyageur</p>
        <h1 id="traveler-title" class="traveler-title">Commander une Box</h1>
        <p class="delivery-minimum">
          <strong>Délai minimum de livraison : 24 h.</strong>
        </p>
      </template>

      <nav
        v-if="props.orderMode || props.orderThanksMode"
        class="traveler-progress"
        aria-label="Progression de la commande"
      >
        <ol>
          <li
            :class="{ 'is-current': showOrderForm && !pendingDelivery }"
            :aria-current="showOrderForm && !pendingDelivery ? 'step' : undefined"
          >
            <span>1</span>Choix
          </li>
          <li
            :class="{ 'is-current': !!pendingDelivery }"
            :aria-current="pendingDelivery ? 'step' : undefined"
          >
            <span>2</span>Paiement
          </li>
          <li
            :class="{ 'is-current': orderSubmitted }"
            :aria-current="orderSubmitted ? 'step' : undefined"
          >
            <span>3</span>Confirmation
          </li>
          <li
            :class="{ 'is-current': props.orderThanksMode }"
            :aria-current="props.orderThanksMode ? 'step' : undefined"
          >
            <span>4</span>Préparation
          </li>
        </ol>
      </nav>

      <p v-if="loading" class="flow-status" role="status">Chargement de la box…</p>

      <section v-if="box" class="traveler-accommodation-card" aria-label="Votre logement">
        <span>Votre logement</span>
        <strong>{{ box.apartmentName ?? 'Logement associé à la Box' }}</strong>
      </section>

      <template v-if="box && !props.orderMode">
        <figure class="traveler-featured-offer">
          <img :src="offerImage" alt="Box OTEM présentée dans un logement lumineux" />
          <figcaption>
            <span>{{ box.offerDisplayName }}</span>
            <div
              v-if="box.purchaseAvailable !== false"
              class="traveler-price"
              aria-label="Prix de l’offre"
            >
              <strong>{{ formattedPrice }}</strong>
            </div>
          </figcaption>
        </figure>

        <p v-if="box.purchaseAvailable === false" class="flow-notice" role="status">
          Cette Box n’a pas encore d’offre disponible. Vous pouvez consulter le catalogue.
        </p>

        <p v-if="isCheckoutCancel" class="flow-notice flow-notice-warning" role="status">
          Paiement interrompu. Aucune nouvelle tentative ne sera créée tant que le serveur ne
          confirme pas l’échec.
        </p>

        <div v-if="entitlement?.status === 'PENDING'" class="flow-panel" role="status">
          <h2>Paiement en attente</h2>
          <p>Nous attendons la confirmation sécurisée du prestataire.</p>
          <button
            v-if="canResumeCheckout"
            class="button button-primary traveler-primary"
            data-testid="resume-checkout"
            type="button"
            @click="resumeCheckout"
          >
            Reprendre le paiement
          </button>
          <button
            class="button button-secondary traveler-secondary"
            type="button"
            :disabled="busy"
            @click="refreshEntitlement"
          >
            {{ statusRefreshing ? 'Vérification…' : 'Vérifier le paiement' }}
          </button>
        </div>

        <div
          v-else-if="entitlement?.status === 'FAILED'"
          class="flow-panel flow-panel-error"
          role="alert"
        >
          <h2>Paiement non confirmé</h2>
          <p>Aucun accès n’a été délivré. Vous pouvez réessayer.</p>
        </div>

        <div
          v-else-if="entitlement?.status === 'ENTITLED'"
          class="flow-panel flow-panel-success"
          role="status"
          aria-atomic="true"
        >
          <h2>Paiement validé</h2>
          <p v-if="codeLoading">Récupération sécurisée de votre code…</p>
          <div v-if="delivery" class="lock-code" aria-live="polite">
            <span>Code Box</span>
            <strong>{{ delivery.lockCode }}</strong>
            <small v-if="delivery.deliveryKind === 'PREPAID_DELIVERY'"
              >Commande prépayée : retrouvez ce code en scannant la Box.</small
            >
            <small v-else>Récupérable pendant 24 h.</small>
          </div>
          <p
            v-if="delivery && delivery.deliveryKind !== 'PREPAID_DELIVERY'"
            class="code-recovery-note"
            data-testid="redisplays-remaining"
          >
            {{ redisplaysRemaining }} réaffichage{{
              redisplaysRemaining > 1 ? 's' : ''
            }}
            restant{{ redisplaysRemaining > 1 ? 's' : '' }} sur 3.
          </p>
        </div>

        <button
          v-if="canStartCheckout"
          class="button button-primary traveler-primary"
          data-testid="start-checkout"
          type="button"
          :disabled="checkoutStarting"
          @click="startCheckout"
        >
          {{ entitlement?.status === 'FAILED' ? 'Réessayer le paiement' : 'Paiement sécurisé' }}
        </button>
        <section class="additional-order" aria-label="Catalogue de Box">
          <a
            class="button button-primary traveler-primary"
            data-testid="open-dedicated-order-page"
            :href="anotherOrderPath"
          >
            Voir le catalogue et commander une Box
          </a>
        </section>
      </template>

      <section
        v-if="pendingDelivery && props.orderMode"
        class="traveler-order-summary"
        data-testid="delivery-payment-page"
        aria-label="Paiement de la commande"
      >
        <p v-if="deliveryPaymentTiming">
          Pour une livraison dans les cinq prochains jours, le montant est réservé sur votre
          carte puis prélevé à la livraison. Au-delà, il est prélevé immédiatement. La
          préparation nécessite une empreinte suffisamment longue.
        </p>
        <h2>{{ pendingDeliveryOffer?.displayName ?? 'Offre indisponible' }}</h2>
        <p v-if="pendingDelivery.travelerComment" class="order-comment">
          <strong>Commentaire :</strong> {{ pendingDelivery.travelerComment }}
        </p>
        <dl>
          <div>
            <dt>Nom</dt>
            <dd>{{ pendingDelivery.travelerName }}</dd>
          </div>
          <div>
            <dt>Logement</dt>
            <dd>{{ pendingDelivery.apartmentName }}</dd>
          </div>
          <div>
            <dt>Livraison au plus tard</dt>
            <dd>{{ String(pendingDelivery.deliveryDeadlineAt).slice(0, 10) }}</dd>
          </div>
          <div v-if="pendingDeliveryOffer">
            <dt>Total à payer</dt>
            <dd>
              {{
                formatAmount(pendingDeliveryOffer.amountMinor, pendingDeliveryOffer.currency)
              }}
            </dd>
          </div>
        </dl>
        <button
          type="button"
          class="button button-primary traveler-primary"
          data-testid="resume-delivery-payment"
          :disabled="busy || !deliveryPrepaymentAvailable"
          @click="resumeDeliveryPayment()"
        >
          {{
            deliveryRequestSaving
              ? 'Vérification du paiement…'
              : localSandbox
                ? 'Valider le paiement de test'
                : 'Reprendre le paiement sécurisé'
          }}
        </button>
      </section>

      <p
        v-if="
          props.webMode &&
          catalogueReady &&
          !pendingDelivery &&
          !orderSubmitted &&
          (!boxOffers.length || !webApartments.length)
        "
        role="status"
      >
        Le catalogue ouvrira prochainement. Aucune commande n’est disponible pour le moment.
      </p>
      <form
        v-if="
          (box ||
            (props.webMode && catalogueReady && boxOffers.length && webApartments.length)) &&
          showOrderForm &&
          !pendingDelivery
        "
        class="delivery-request-form dedicated-order-form"
        data-testid="delivery-request-form"
        @submit.prevent="submitDeliveryRequest"
      >
        <ApartmentPicker
          v-if="props.webMode"
          v-model="apartmentName"
          :apartments="webApartments.map((item) => item.apartment)"
        />
        <label class="traveler-native-offer-label" for="delivery-box-offer"
          >Offre et Prix</label
        >
        <select
          id="delivery-box-offer"
          v-model="selectedBoxOfferId"
          class="traveler-native-offer-select"
          required
        >
          <option value="" disabled>Choisissez une offre</option>
          <option v-for="offer in availableOffers" :key="offer.offerId" :value="offer.offerId">
            {{ offer.displayName }} — {{ formatAmount(offer.amountMinor, offer.currency) }}
          </option>
        </select>
        <fieldset class="traveler-offer-picker">
          <legend>Choisissez votre Box</legend>
          <div class="traveler-offer-rail" data-testid="traveler-offer-rail">
            <label
              v-for="offer in availableOffers"
              :key="`card-${offer.offerId}`"
              class="traveler-offer-option"
              :class="{ 'is-selected': selectedBoxOfferId === offer.offerId }"
              :data-testid="`traveler-offer-card-${offer.offerId}`"
            >
              <input v-model="selectedBoxOfferId" type="radio" :value="offer.offerId" />
              <img :src="photoForOffer(offer)" :alt="`Photo de l’offre ${offer.displayName}`" />
              <span class="traveler-offer-option-copy">
                <strong>{{ offer.displayName }}</strong>
                <small>{{ offer.products.map((product) => product.name).join(' · ') }}</small>
                <b>{{ formatAmount(offer.amountMinor, offer.currency) }}</b>
                <em>{{
                  selectedBoxOfferId === offer.offerId ? 'Sélectionnée' : 'Sélectionner'
                }}</em>
              </span>
            </label>
          </div>
        </fieldset>
        <section
          v-if="selectedBoxOffer"
          class="traveler-offer-content"
          data-testid="traveler-offer-content"
          aria-live="polite"
        >
          <h2>{{ selectedBoxOffer.displayName }}</h2>
          <ul>
            <li v-for="product in selectedBoxOffer.products" :key="product.name">
              {{ product.name }} × {{ product.quantity }}
            </li>
          </ul>
          <p>* suivant stock disponible</p>
        </section>
        <label for="delivery-traveler-name">Nom</label>
        <input
          id="delivery-traveler-name"
          v-model="travelerName"
          maxlength="128"
          autocomplete="name"
          required
        />
        <label for="traveler-comment">Commentaire pour la commande (facultatif)</label>
        <textarea
          id="traveler-comment"
          v-model="travelerComment"
          maxlength="500"
          rows="3"
          placeholder="Ex. : remplacer Coca par Orangina"
        />
        <p class="order-comment-hint">
          Visible par le préparateur et l’administrateur. Les demandes restent soumises à
          disponibilité.
        </p>

        <template v-if="!props.webMode && !box?.apartmentName">
          <label for="delivery-apartment-name">Appartement / nom du logement</label>
          <input
            id="delivery-apartment-name"
            v-model="apartmentName"
            maxlength="128"
            required
          />
        </template>
        <div
          v-if="!deliveryCalendarVisible"
          class="traveler-delivery-date"
          data-testid="default-delivery-date"
        >
          <span>Date maximale de livraison</span>
          <strong>{{ formattedDeliveryDate }}</strong>
          <button
            class="button button-secondary traveler-secondary"
            data-testid="choose-later-delivery-date"
            type="button"
            aria-controls="delivery-deadline"
            @click="openDeliveryCalendar"
          >
            Choisir une autre date
          </button>
        </div>
        <label v-show="deliveryCalendarVisible" for="delivery-deadline"
          >Date maximale de livraison</label
        >
        <input
          v-show="deliveryCalendarVisible"
          id="delivery-deadline"
          ref="deliveryDateInput"
          v-model="deliveryDeadlineDate"
          type="date"
          :min="minimumDeliveryDate"
          @click="showDeliveryDatePicker"
          required
        />
        <small v-show="deliveryCalendarVisible" data-testid="delivery-date-help">
          Les dates ne respectant pas le délai minimum de 24 h sont désactivées.
        </small>
        <button
          v-if="deliveryCalendarVisible"
          class="button button-secondary traveler-secondary"
          data-testid="restore-default-delivery-date"
          type="button"
          @click="restoreDefaultDeliveryDate"
        >
          Revenir à la date par défaut
        </button>
        <aside
          v-if="selectedBoxOffer"
          class="traveler-order-summary"
          aria-label="Récapitulatif"
        >
          <span>Récapitulatif</span>
          <strong>{{ selectedBoxOffer.displayName }}</strong>
          <dl>
            <div>
              <dt>Logement</dt>
              <dd>{{ apartmentName || box?.apartmentName || 'À confirmer' }}</dd>
            </div>
            <div>
              <dt>Livraison</dt>
              <dd>{{ deliveryDeadlineDate }}</dd>
            </div>
            <div>
              <dt>Total</dt>
              <dd>
                {{ formatAmount(selectedBoxOffer.amountMinor, selectedBoxOffer.currency) }}
              </dd>
            </div>
          </dl>
        </aside>
        <label class="delivery-consent" for="delivery-absence-consent">
          <input
            id="delivery-absence-consent"
            v-model="absentEntryAuthorized"
            type="checkbox"
            required
          />
          <span>{{
            'J’autorise l’équipe à entrer dans le logement pour livrer la Box en mon absence.'
          }}</span>
        </label>
        <p
          v-if="!busy && deliveryValidationMessage"
          id="delivery-validation-help"
          data-testid="delivery-validation-help"
          role="status"
        >
          {{ deliveryValidationMessage }}
        </p>
        <button
          class="button button-primary traveler-primary"
          data-testid="submit-delivery-request"
          type="submit"
          :disabled="!canSubmitDeliveryRequest"
          :aria-describedby="
            !busy && deliveryValidationMessage ? 'delivery-validation-help' : undefined
          "
        >
          Commander et payer
        </button>
      </form>

      <p v-if="error" class="flow-error" role="alert">{{ error }}</p>
    </section>
    <TravelerOrders :revision="ordersRevision" :initially-open="isSupplementReturn" />
  </main>
</template>
